/**
 * ReporteFiscalizacionIA.jsx · v101.255.0 · Reportes › Fiscalización IA
 * Indicadores (con su denominador), desgloses, rankings de reglas y motivos, listado filtrable / ordenable / paginado,
 * detalle por OT (validación por regla con evidencia, ejecuciones históricas, revisiones manuales y llamadas a la IA)
 * y exportación a Excel / CSV respetando los filtros.
 */
import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Download, RefreshCw, Search, X, ChevronLeft, ChevronRight, ArrowUpDown, ShieldCheck } from "lucide-react";

const ESTADO_CLS = {
  "Pendiente": "bg-slate-100 text-slate-600",
  "En proceso": "bg-sky-50 text-sky-700",
  "Fiscalización aprobada": "bg-emerald-50 text-emerald-700",
  "Aprobada con observaciones": "bg-teal-50 text-teal-700",
  "Rechazada": "bg-rose-50 text-rose-700",
  "Revisión manual requerida": "bg-amber-50 text-amber-700",
  "Error de procesamiento": "bg-orange-50 text-orange-700",
};
const RES_CLS = {
  "Correcto": "text-emerald-700", "Incorrecto": "text-rose-700", "No verificable": "text-amber-700",
  "Error": "text-orange-700", "No evaluada": "text-slate-500",
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-EC", {
  day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil",
}) : "—");
const hoy = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);
const errMsg = (e, d) => String(e?.response?.data?.detail || d);
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

const FILTROS = [
  ["sucursal", "Sucursal / ciudad"], ["zona", "Zona"], ["actividad", "Tipo de actividad"], ["tecnico", "Técnico"],
  ["cuadrilla", "Cuadrilla"], ["contratista", "Contratista"], ["estado_operativo", "Estado operativo"],
  ["equipo_ventas", "Equipo de ventas"],
];
const COLS = [
  ["orden", "OT"], ["odoo_task_id", "Odoo"], ["cliente", "Cliente"], ["suscripcion", "Suscripción"],
  ["actividad", "Actividad"], ["sucursal", "Sucursal"], ["tecnico", "Técnico"], ["cuadrilla", "Cuadrilla"],
  ["estado_ia", "Estado IA"], ["score", "Score"], ["n_fail", "Incorrectas"], ["revision_manual", "Rev. manual"],
  ["fin_fiscalizacion", "Fin fiscalización"], ["horas_fiscalizacion", "Horas"],
];
const DESGLOSES = [
  ["por_tecnico", "Técnico"], ["por_cuadrilla", "Cuadrilla"], ["por_contratista", "Contratista"],
  ["por_actividad", "Actividad"], ["por_sucursal", "Sucursal"], ["por_zona", "Zona"],
];

function Kpi({ label, value, sub, cls = "" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold mt-0.5 ${cls}`}>{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{sub}</div>}
    </div>
  );
}

export default function ReporteFiscalizacionIA() {
  const [f, setF] = useState({ desde: haceDias(30), hasta: hoy(), q: "", estado_ia: "", revision_manual: "", solo_error: "" });
  const [opc, setOpc] = useState({});
  const [res, setRes] = useState(null);
  const [tab, setTab] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [orden, setOrden] = useState({ campo: "fin_fiscalizacion", desc: true });
  const [desg, setDesg] = useState("por_tecnico");
  const [busy, setBusy] = useState("");
  const [det, setDet] = useState(null);
  const size = 50;

  const params = useMemo(() => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) p[k] = v; });
    return p;
  }, [f]);

  const cargar = async (pg = 1) => {
    setBusy("cargar");
    try {
      const [r1, r2] = await Promise.all([
        api.get("/v1/reportes/fiscalizacion-ia/resumen", { params, timeout: 180000 }),
        api.get("/v1/reportes/fiscalizacion-ia/tareas", {
          params: { ...params, page: pg, size, orden: orden.campo, desc: orden.desc }, timeout: 180000,
        }),
      ]);
      setRes(r1.data); setTab(r2.data); setPage(pg);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo cargar el reporte"));
    } finally { setBusy(""); }
  };
  const cargarOpc = async () => {
    try {
      const { data } = await api.get("/v1/reportes/fiscalizacion-ia/opciones", { params: { desde: f.desde, hasta: f.hasta } });
      setOpc(data || {});
    } catch { /* filtros sin opciones */ }
  };
  useEffect(() => { cargar(1); cargarOpc(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { if (res) cargar(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orden]);

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const limpiar = () => setF({ desde: haceDias(30), hasta: hoy(), q: "", estado_ia: "", revision_manual: "", solo_error: "" });
  const ordenar = (campo) => setOrden((o) => ({ campo, desc: o.campo === campo ? !o.desc : true }));

  const exportar = async (formato, vista = "tareas") => {
    setBusy(`exp-${formato}-${vista}`);
    try {
      const resp = await api.get("/v1/reportes/fiscalizacion-ia/export", {
        params: { ...params, formato, vista, historico: 1 }, responseType: "blob", timeout: 300000,
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fiscalizacion_ia_${formato === "csv" ? vista + "_" : ""}${f.desde}_${f.hasta}.${formato}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo exportar"));
    } finally { setBusy(""); }
  };

  const abrir = async (tid) => {
    setDet({ cargando: true });
    try {
      const { data } = await api.get(`/v1/reportes/fiscalizacion-ia/tareas/${tid}`, { timeout: 120000 });
      setDet(data);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo abrir el detalle")); setDet(null);
    }
  };

  const t = res?.totales || {};
  const tasas = res?.tasas || {};
  const paginas = Math.max(1, Math.ceil((tab.total || 0) / size));
  const evo = res?.evolucion || [];
  const maxEvo = Math.max(1, ...evo.map((x) => x.total));

  return (
    <Layout>
      <div className="space-y-4" data-testid="reporte-fiscalizacion-ia">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-teal-600" />
            <div>
              <h1 className="text-xl font-semibold text-slate-800">Reportes › Fiscalización IA</h1>
              <p className="text-xs text-slate-500">Resultado de la fiscalización por IA de cada OT, con trazabilidad de ejecuciones y revisiones manuales.</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => exportar("xlsx")} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm disabled:opacity-50">
              {busy === "exp-xlsx-tareas" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Excel
            </button>
            {[["tareas", "CSV tareas"], ["validaciones", "CSV validaciones"], ["ejecuciones", "CSV historial"]].map(([v, l]) => (
              <button key={v} onClick={() => exportar("csv", v)} disabled={!!busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm disabled:opacity-50">
                {busy === `exp-csv-${v}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {l}
              </button>
            ))}
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2 text-sm">
            <label className="flex flex-col"><span className="text-[11px] text-slate-500">Desde</span>
              <input type="date" value={f.desde} onChange={set("desde")} className="border rounded px-2 py-1" /></label>
            <label className="flex flex-col"><span className="text-[11px] text-slate-500">Hasta</span>
              <input type="date" value={f.hasta} onChange={set("hasta")} className="border rounded px-2 py-1" /></label>
            <label className="flex flex-col col-span-2"><span className="text-[11px] text-slate-500">Buscar (OT, ID Odoo, ID AlfaField, cliente, suscripción)</span>
              <input value={f.q} onChange={set("q")} onKeyDown={(e) => e.key === "Enter" && cargar(1)} placeholder="00336180…" className="border rounded px-2 py-1" /></label>
            <label className="flex flex-col"><span className="text-[11px] text-slate-500">Estado IA</span>
              <select value={f.estado_ia} onChange={set("estado_ia")} className="border rounded px-2 py-1">
                <option value="">Todos</option>
                {(opc.estados || Object.keys(ESTADO_CLS)).map((e) => <option key={e} value={e}>{e}</option>)}
              </select></label>
            <label className="flex flex-col"><span className="text-[11px] text-slate-500">Revisión manual</span>
              <select value={f.revision_manual} onChange={set("revision_manual")} className="border rounded px-2 py-1">
                <option value="">Todas</option><option value="si">Con revisión</option><option value="no">Sin revisión</option>
              </select></label>
            <label className="flex items-end gap-1.5 pb-1"><input type="checkbox" checked={!!f.solo_error}
              onChange={(e) => setF((x) => ({ ...x, solo_error: e.target.checked ? "1" : "" }))} />
              <span className="text-xs">Solo con error</span></label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2 text-sm">
            {FILTROS.map(([k, l]) => (
              <label key={k} className="flex flex-col"><span className="text-[11px] text-slate-500">{l}</span>
                <select value={f[k] || ""} onChange={set(k)} className="border rounded px-2 py-1">
                  <option value="">Todos</option>
                  {(opc[k] || []).map((v) => <option key={v} value={v}>{v}</option>)}
                </select></label>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => { cargar(1); cargarOpc(); }} disabled={busy === "cargar"} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-600 text-white text-sm disabled:opacity-50">
              {busy === "cargar" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Aplicar filtros
            </button>
            <button onClick={limpiar} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm"><RefreshCw className="w-4 h-4" /> Limpiar</button>
          </div>
        </div>

        {/* Indicadores */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
          <Kpi label="OTs elegibles" value={t.elegibles} sub="solucionadas / con formulario en el rango" />
          <Kpi label="Enviadas a fiscalizar" value={t.enviadas_a_fiscalizacion} sub="con formulario y fotos" />
          <Kpi label="Fiscalizadas" value={t.fiscalizadas} sub="con resultado final" />
          <Kpi label="Aprobación" value={pct(tasas.aprobacion?.valor)} sub={`÷ ${tasas.aprobacion?.denominador || "fiscalizadas"}`} cls="text-emerald-700" />
          <Kpi label="Rechazo" value={pct(tasas.rechazo?.valor)} sub={`÷ ${tasas.rechazo?.denominador || "fiscalizadas"}`} cls="text-rose-700" />
          <Kpi label="Revisión manual" value={pct(tasas.revision_manual?.valor)} sub={`÷ ${tasas.revision_manual?.denominador || "enviadas"}`} cls="text-amber-700" />
          <Kpi label="Error" value={pct(tasas.error?.valor)} sub={`÷ ${tasas.error?.denominador || "enviadas"}`} cls="text-orange-700" />
          <Kpi label="Tiempo promedio" value={res?.tiempo_promedio_horas != null ? `${res.tiempo_promedio_horas} h` : "—"} sub="del envío al resultado" />
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.keys(ESTADO_CLS).map((e) => (
            <button key={e} onClick={() => setF((x) => ({ ...x, estado_ia: x.estado_ia === e ? "" : e }))}
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${ESTADO_CLS[e]} ${f.estado_ia === e ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}>
              {e}: {t[e] ?? 0}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Desgloses */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-3">
            <div className="flex gap-1 flex-wrap mb-2">
              {DESGLOSES.map(([k, l]) => (
                <button key={k} onClick={() => setDesg(k)} className={`px-2.5 py-1 rounded text-xs ${desg === k ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-700"}`}>Por {l.toLowerCase()}</button>
              ))}
            </div>
            <div className="overflow-auto max-h-80">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-slate-600"><tr>
                  <th className="text-left p-1.5">{DESGLOSES.find((d) => d[0] === desg)?.[1]}</th><th className="p-1.5">Total</th>
                  <th className="p-1.5">Aprobadas</th><th className="p-1.5">Con obs.</th><th className="p-1.5">Rechazadas</th>
                  <th className="p-1.5">Rev. manual</th><th className="p-1.5">Error</th><th className="p-1.5">% aprob.</th><th className="p-1.5">% rechazo</th>
                </tr></thead>
                <tbody>
                  {Object.entries(res?.[desg] || {}).map(([k, v]) => (
                    <tr key={k} className="border-t">
                      <td className="p-1.5">{k}</td><td className="p-1.5 text-center font-medium">{v.total}</td>
                      <td className="p-1.5 text-center">{v["Fiscalización aprobada"]}</td><td className="p-1.5 text-center">{v["Aprobada con observaciones"]}</td>
                      <td className="p-1.5 text-center">{v["Rechazada"]}</td><td className="p-1.5 text-center">{v["Revisión manual requerida"]}</td>
                      <td className="p-1.5 text-center">{v["Error de procesamiento"]}</td>
                      <td className="p-1.5 text-center text-emerald-700">{pct(v.tasa_aprobacion)}</td><td className="p-1.5 text-center text-rose-700">{pct(v.tasa_rechazo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {desg === "por_cuadrilla" && <p className="text-[11px] text-slate-500 mt-1">Cuadrilla histórica: la registrada al momento de cada ejecución (desde v101.255.0); antes, la vigente.</p>}
          </div>
          {/* Rankings */}
          <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-3">
            {[["reglas_mas_incumplidas", "Reglas más incumplidas"], ["motivos_rechazo", "Motivos de rechazo más frecuentes"]].map(([k, l]) => (
              <div key={k}>
                <div className="text-xs font-semibold text-slate-700 mb-1">{l}</div>
                {(res?.[k] || []).slice(0, 8).map(([n, c]) => (
                  <div key={n} className="flex justify-between gap-2 text-xs border-t py-1"><span className="truncate" title={n}>{n}</span><span className="font-medium">{c}</span></div>
                ))}
                {!(res?.[k] || []).length && <div className="text-xs text-slate-400">Sin datos</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Evolución */}
        {evo.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-slate-700 mb-2">Evolución diaria (fiscalizadas por fecha de resultado)</div>
            <div className="flex items-end gap-1 h-28 overflow-x-auto">
              {evo.map((d) => (
                <div key={d.fecha} className="flex flex-col items-center min-w-[22px]" title={`${d.fecha}: ${d.total} · aprobadas ${d.aprobadas} · rechazadas ${d.rechazadas}`}>
                  <div className="w-4 flex flex-col-reverse" style={{ height: `${(d.total / maxEvo) * 88}px` }}>
                    <div className="bg-emerald-500" style={{ height: `${d.total ? (d.aprobadas / d.total) * 100 : 0}%` }} />
                    <div className="bg-rose-500" style={{ height: `${d.total ? (d.rechazadas / d.total) * 100 : 0}%` }} />
                    <div className="bg-slate-300 flex-1" />
                  </div>
                  <span className="text-[9px] text-slate-500 mt-0.5">{d.fecha.slice(8, 10)}</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-1"><span className="inline-block w-2 h-2 bg-emerald-500 mr-1" />Aprobadas <span className="inline-block w-2 h-2 bg-rose-500 mx-1" />Rechazadas <span className="inline-block w-2 h-2 bg-slate-300 mx-1" />Otras</div>
          </div>
        )}

        {/* Listado */}
        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between p-2 text-xs text-slate-600">
            <span>{tab.total || 0} OTs · página {page} de {paginas}</span>
            <div className="flex gap-1">
              <button disabled={page <= 1 || busy} onClick={() => cargar(page - 1)} className="p-1 border rounded disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
              <button disabled={page >= paginas || busy} onClick={() => cargar(page + 1)} className="p-1 border rounded disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600"><tr>
                {COLS.map(([k, l]) => (
                  <th key={k} className="text-left p-1.5 whitespace-nowrap cursor-pointer select-none" onClick={() => ordenar(k)}>
                    {l} <ArrowUpDown className={`inline w-3 h-3 ${orden.campo === k ? "text-slate-800" : "text-slate-300"}`} />
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {(tab.items || []).map((x) => (
                  <tr key={x.odoo_task_id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => abrir(x.odoo_task_id)}>
                    {COLS.map(([k]) => (
                      <td key={k} className="p-1.5 whitespace-nowrap max-w-[200px] truncate">
                        {k === "estado_ia" ? <span className={`px-2 py-0.5 rounded-full ${ESTADO_CLS[x[k]] || ""}`}>{x[k]}</span>
                          : k === "fin_fiscalizacion" ? fmt(x[k])
                            : k === "score" ? (x[k] != null ? `${x[k]}%` : "—")
                              : (x[k] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
                {!(tab.items || []).length && <tr><td colSpan={COLS.length} className="p-6 text-center text-slate-400">{busy === "cargar" ? "Cargando…" : "Sin OTs con estos filtros"}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detalle */}
      {det && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={() => setDet(null)}>
          <div className="w-full max-w-3xl h-full bg-white overflow-y-auto p-4 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="fia-detalle">
            {det.cargando ? <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div> : (
              <>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-semibold">OT {det.tarea.orden} · Odoo {det.tarea.odoo_task_id}</div>
                    <div className="text-xs text-slate-500">{det.tarea.actividad} · {det.tarea.sucursal} · {det.tarea.tecnico || "sin técnico"}{det.tarea.cuadrilla ? ` · ${det.tarea.cuadrilla}` : ""}</div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${ESTADO_CLS[det.tarea.estado_ia] || ""}`}>{det.tarea.estado_ia}</span>
                      {det.tarea.score != null && <span className="text-xs">Score {det.tarea.score}%</span>}
                      <a href={`/ot-smart/${det.tarea.odoo_task_id}`} className="text-xs text-teal-700 underline">Abrir OT</a>
                    </div>
                    {det.tarea.motivo && <div className="text-xs text-slate-700 mt-1">{det.tarea.motivo}</div>}
                  </div>
                  <button onClick={() => setDet(null)} className="p-1"><X className="w-5 h-5" /></button>
                </div>

                <section>
                  <h3 className="text-sm font-semibold mb-1">Validaciones por regla</h3>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600"><tr><th className="text-left p-1.5">Tipo</th><th className="text-left p-1.5">Regla</th><th className="p-1.5">Resultado</th><th className="p-1.5">Conf.</th><th className="text-left p-1.5">Observación</th><th className="p-1.5">Evidencia</th></tr></thead>
                    <tbody>
                      {det.validaciones.map((v, i) => (
                        <tr key={i} className="border-t align-top">
                          <td className="p-1.5">{v.tipo}</td><td className="p-1.5">{v.regla}{v.seccion ? <div className="text-[10px] text-slate-400">{v.seccion}</div> : null}</td>
                          <td className={`p-1.5 text-center font-medium ${RES_CLS[v.resultado] || ""}`}>{v.resultado}</td>
                          <td className="p-1.5 text-center">{v.confianza != null ? Number(v.confianza).toFixed(2) : "—"}</td>
                          <td className="p-1.5">{v.observacion || "—"}</td>
                          <td className="p-1.5 text-center">{v.evidencia_url && /^(https?:|data:image)/.test(v.evidencia_url)
                            ? <a href={v.evidencia_url} target="_blank" rel="noreferrer"><img src={v.evidencia_url} alt="" className="w-14 h-14 object-cover rounded border inline-block" /></a> : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>

                <section>
                  <h3 className="text-sm font-semibold mb-1">Ejecuciones (historial · {det.ejecuciones.length})</h3>
                  {det.ejecuciones.map((e) => (
                    <div key={e.ejecucion_id} className="border rounded p-2 mb-1 text-xs">
                      <div className="flex justify-between flex-wrap gap-1">
                        <span>{fmt(e.creado_at)} · {e.origen} · <span className={`px-1.5 rounded ${ESTADO_CLS[e.estado] || ""}`}>{e.estado}</span>{e.score != null ? ` · ${e.score}%` : ""}</span>
                        <span className="text-slate-400 font-mono">{String(e.ejecucion_id).slice(0, 8)}</span>
                      </div>
                      <div className="text-slate-500">Correctas {e.n_ok} · Incorrectas {e.n_fail} · No verif. {e.n_no_verif} · Error {e.n_error} · Faltantes {e.n_faltantes} · {e.motor} · {e.version_reglas}</div>
                      <div className="text-slate-500">{e.tecnico_nombre || "—"}{e.cuadrilla ? ` · ${e.cuadrilla}` : ""}{e.contratista ? ` · ${e.contratista}` : ""}</div>
                    </div>
                  ))}
                  {!det.ejecuciones.length && <div className="text-xs text-slate-400">Sin ejecuciones registradas (OT fiscalizada antes de v101.255.0 y sin carga inicial).</div>}
                </section>

                <section>
                  <h3 className="text-sm font-semibold mb-1">Revisiones manuales</h3>
                  {det.revisiones.map((r, i) => (
                    <div key={i} className="border-t py-1 text-xs">
                      <b>{r.verdict === "approved" ? "Aprobada" : r.verdict === "rejected" ? "Rechazada" : r.verdict}</b> · {r.decided_by_email} · {fmt(r.decided_at)}
                      {(r.estado_anterior || r.estado_nuevo) && <span className="text-slate-500"> · {r.estado_anterior || "—"} → {r.estado_nuevo || "—"}</span>}
                      {r.note && <div className="text-slate-600">{r.note}</div>}
                    </div>
                  ))}
                  {!det.revisiones.length && <div className="text-xs text-slate-400">Sin revisiones manuales.</div>}
                </section>

                <section>
                  <h3 className="text-sm font-semibold mb-1">Llamadas a la IA (por foto)</h3>
                  {det.llamadas_ia.slice(0, 50).map((c, i) => (
                    <div key={i} className="border-t py-1 text-xs">
                      {fmt(c.creado_at)} · {c.field_key || "—"} · <span className={RES_CLS[{ pass: "Correcto", fail: "Incorrecto", unverifiable: "No verificable", error: "Error" }[c.resultado]] || ""}>{c.resultado}</span>
                      {c.confianza != null ? ` · ${Number(c.confianza).toFixed(2)}` : ""}{c.ms ? ` · ${(c.ms / 1000).toFixed(1)} s` : ""}
                      {c.razon && <div className="text-slate-500">{c.razon}</div>}
                    </div>
                  ))}
                  {!det.llamadas_ia.length && <div className="text-xs text-slate-400">Sin llamadas registradas (se registran desde v101.255.0).</div>}
                </section>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
